// src/routes/AppRouter.jsx
import React, { useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AppLayout, PublicLayout } from "../components/layout";
import LandingPage from "../components/LandingPage";
import AboutPage from "../components/AboutPage";
import ContactPage from "../components/ContactPage";
import PrivacyPolicyPage from "../components/PrivacyPolicyPage";
import TermsOfServicePage from "../components/TermsOfServicePage";
import SecurityPage from "../components/SecurityPage";
import PortfolioPage from "../components/Portfolio/PortfolioPage";
import LoginPage from "../components/LoginPage";
import ProtectedRoute from "../components/ProtectedRoute";
import VerifyEmailPage from "../components/VerifyEmailPage";
import ForgotPasswordPage from "../components/ForgotPasswordPage";
import ResetPasswordPage from "../components/ResetPasswordPage";
import ProfilePage from "../components/ProfilePage";
import SettingsPage from "../components/SettingsPage";
import AccountsPage from "../components/AccountsPage";
import InvestmentsPage from "../components/Portfolio/InvestmentsPage";
import GoalsPage from "../components/GoalsPage";
import ProjectionsPage from "../components/ProjectionsPage";
import SubscriptionRequired from "../components/SubscriptionRequired";
import SubscriptionSuccess from "../components/SubscriptionSuccess";
import SubscriptionCancel from "../components/SubscriptionCancel";
import DemoAppLayout from "../components/demo/DemoAppLayout";
import DemoPortfolioTab from "../components/demo/DemoPortfolioTab";
import DemoAccountsTab from "../components/demo/DemoAccountsTab";
import DemoInvestmentsTab from "../components/demo/DemoInvestmentsTab";
import DemoGoalsTab from "../components/demo/DemoGoalsTab";
import DemoProjectionsTab from "../components/demo/DemoProjectionsTab";
import DemoInsightsTab from "../components/demo/DemoInsightsTab";
import AdminPage from "../components/admin/AdminPage";
import InsightsPage from "../components/InsightsPage";
import ChangelogPage from "../components/ChangelogPage";
import BlogListPage from "../components/blog/BlogListPage";
import BlogPostPage from "../components/blog/BlogPostPage";
import { PageErrorBoundary } from "../components/ui/ErrorBoundary";

// Wrapper for public pages with PublicLayout
function PublicPage({ children }) {
    return <PublicLayout>{children}</PublicLayout>;
}

// Wrapper for authenticated pages with AppLayout
function AppPage({ children, requireSubscription = true }) {
    return (
        <ProtectedRoute requireSubscription={requireSubscription}>
            <AppLayout>{children}</AppLayout>
        </ProtectedRoute>
    );
}

// Wrapper for demo pages with DemoAppLayout
function DemoPage({ children }) {
    return <DemoAppLayout>{children}</DemoAppLayout>;
}

function usePageTracking() {
    const location = useLocation();
    useEffect(() => {
        if (window.gtag) {
            window.gtag('config', 'G-VL21F2Q7MX', {
                page_path: location.pathname + location.search,
            });
        }
    }, [location]);
}

export default function AppRouter() {
    usePageTracking();

    return (
        <Routes>
            {/* Public routes */}
            <Route path="/" element={<PublicPage><LandingPage /></PublicPage>} />
            <Route path="/about" element={<PublicPage><AboutPage /></PublicPage>} />
            <Route path="/contact" element={<PublicPage><ContactPage /></PublicPage>} />
            <Route path="/privacy" element={<PublicPage><PrivacyPolicyPage /></PublicPage>} />
            <Route path="/terms" element={<PublicPage><TermsOfServicePage /></PublicPage>} />
            <Route path="/security" element={<PublicPage><SecurityPage /></PublicPage>} />
            <Route path="/changelog" element={<PublicPage><ChangelogPage /></PublicPage>} />
            <Route path="/blog" element={<PublicPage><BlogListPage /></PublicPage>} />
            <Route path="/blog/:slug" element={<PublicPage><BlogPostPage /></PublicPage>} />

            {/* Demo routes with app-like layout */}
            <Route path="/demo" element={<DemoPage><DemoPortfolioTab /></DemoPage>} />
            <Route path="/demo/accounts" element={<DemoPage><DemoAccountsTab /></DemoPage>} />
            <Route path="/demo/investments" element={<DemoPage><DemoInvestmentsTab /></DemoPage>} />
            <Route path="/demo/goals" element={<DemoPage><DemoGoalsTab /></DemoPage>} />
            <Route path="/demo/projections" element={<DemoPage><DemoProjectionsTab /></DemoPage>} />
            <Route path="/demo/insights" element={<DemoPage><DemoInsightsTab /></DemoPage>} />

            {/* Auth routes (minimal layout) */}
            <Route path="/login" element={<PublicPage><LoginPage /></PublicPage>} />
            <Route path="/signup" element={<PublicPage><LoginPage isSignup={true} /></PublicPage>} />
            <Route path="/verify-email" element={<PublicPage><VerifyEmailPage /></PublicPage>} />
            <Route path="/forgot-password" element={<PublicPage><ForgotPasswordPage /></PublicPage>} />
            <Route path="/reset-password" element={<PublicPage><ResetPasswordPage /></PublicPage>} />

            {/* Subscription routes (requires auth but not subscription) */}
            <Route path="/subscribe" element={
                <AppPage requireSubscription={false}>
                    <PageErrorBoundary><SubscriptionRequired /></PageErrorBoundary>
                </AppPage>
            } />
            <Route path="/subscription/success" element={
                <ProtectedRoute requireSubscription={false}>
                    <SubscriptionSuccess />
                </ProtectedRoute>
            } />
            <Route path="/subscription/cancel" element={
                <ProtectedRoute requireSubscription={false}>
                    <SubscriptionCancel />
                </ProtectedRoute>
            } />

            {/* Admin route (requires auth, not subscription; role checked in component) */}
            <Route path="/admin" element={
                <ProtectedRoute requireSubscription={false}>
                    <AppLayout><PageErrorBoundary><AdminPage /></PageErrorBoundary></AppLayout>
                </ProtectedRoute>
            } />

            {/* Protected routes with app layout; each page wrapped in PageErrorBoundary */}
            <Route path="/insights" element={<AppPage><PageErrorBoundary><InsightsPage /></PageErrorBoundary></AppPage>} />
            <Route path="/portfolio" element={<AppPage><PageErrorBoundary><PortfolioPage /></PageErrorBoundary></AppPage>} />
            <Route path="/profile" element={<AppPage requireSubscription={false}><PageErrorBoundary><ProfilePage /></PageErrorBoundary></AppPage>} />
            <Route path="/accounts" element={<AppPage requireSubscription={false}><PageErrorBoundary><AccountsPage /></PageErrorBoundary></AppPage>} />
            <Route path="/settings" element={<AppPage requireSubscription={false}><PageErrorBoundary><SettingsPage /></PageErrorBoundary></AppPage>} />
            <Route path="/investments" element={<AppPage><PageErrorBoundary><InvestmentsPage /></PageErrorBoundary></AppPage>} />
            <Route path="/goals" element={<AppPage><PageErrorBoundary><GoalsPage /></PageErrorBoundary></AppPage>} />
            <Route path="/projections" element={<AppPage><PageErrorBoundary><ProjectionsPage /></PageErrorBoundary></AppPage>} />

        </Routes>
    );
}
